import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

type CaptchaContext = {
  executeCaptcha: (action: string) => Promise<string>;
};

const CaptchaContext = createContext<CaptchaContext>({
  executeCaptcha: () => Promise.reject('CaptchaContext not initialized'),
});

let resolveCaptchaPromise: () => void;
const captchaPromise = new Promise<void>(resolve => {
  resolveCaptchaPromise = resolve;
});

export function CaptchaContextProvider(props: PropsWithChildren) {
  useEffect(() => {
    const tag = document.createElement('script');
    tag.src = `https://www.google.com/recaptcha/api.js?render=${process.env.GOOGLE_RECAPTCHA_PUBLIC_KEY}`;
    // Surface real error details to Datadog instead of an opaque
    // cross-origin "Script error." (google.com sends Access-Control-Allow-Origin).
    tag.crossOrigin = 'anonymous';
    document.head.appendChild(tag);
    tag.onload = () => {
      if (!window.grecaptcha) {
        console.error('Google Recaptcha failed to load');
        resolveCaptchaPromise();
        return;
      }

      window.grecaptcha.ready(() => {
        resolveCaptchaPromise();
      });
    };
  }, []);

  const executeCaptcha = useCallback(async (action: string) => {
    await captchaPromise;
    if (!window.grecaptcha) {
      console.error('Missing grecaptcha');
      throw new Error('Missing grecaptcha');
    }

    return window.grecaptcha.execute(process.env.GOOGLE_RECAPTCHA_PUBLIC_KEY, {
      action,
    });
  }, []);

  const value = useMemo(() => ({ executeCaptcha }), [executeCaptcha]);

  return (
    <CaptchaContext.Provider value={value}>
      {props.children}
    </CaptchaContext.Provider>
  );
}

export function useCaptchaContext() {
  return useContext(CaptchaContext);
}
